<template>
  <!-- Teleported to the body so nested stacking contexts in the app shell
       cannot paint page content over the dialog. -->
  <teleport to="body">
    <div
      v-if="isOpen"
      class="cookie-preferences__overlay tw-flex items-center justify-center p-4"
      @click.self="closeOnBackdrop"
      @mousedown.self="onBackdropMousedown"
    >
      <div
        ref="dialog"
        class="cookie-preferences w-full max-w-xl bg-white rounded-2xl shadow-2xl tw-flex flex-col"
        role="dialog"
        aria-modal="true"
        :aria-label="$t('cookie_consent_preferences_title')"
        tabindex="-1"
      >
        <div class="tw-flex items-start gap-4 p-6 sm:px-8 sm:pt-8 pb-4">
          <div class="cookie-preferences__icon w-11 h-11 rounded-xl tw-flex items-center justify-center shrink-0">
            <svg class="w-6 h-6" fill="none" stroke="currentColor" stroke-width="1.8" viewBox="0 0 24 24" aria-hidden="true">
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                d="M10.5 6h9M4.5 6h2M13.5 12h6M4.5 12h6M17.5 18h2M4.5 18h9"
              />
              <circle cx="8.5" cy="6" r="2.2"/>
              <circle cx="12" cy="12" r="2.2"/>
              <circle cx="15.5" cy="18" r="2.2"/>
            </svg>
          </div>

          <div class="flex-1 min-w-0">
            <h2 class="text-lg sm:text-xl font-bold text-slate-900 m-0">
              {{ $t('cookie_consent_preferences_title') }}
            </h2>
            <p class="text-sm text-slate-600 leading-relaxed mt-2 mb-0">
              {{ $t('cookie_consent_preferences_intro') }}
              {{ $t('cookie_policy_1') }}
              <a
                :href="cookiePolicyUrl"
                target="_blank"
                rel="noopener noreferrer"
                class="text-primary font-semibold underline"
              >{{ $t('cookie_policy') }}</a>
              {{ $t('cookie_policy_2') }}
            </p>
          </div>

          <!--
            Only offered once a decision exists. While consent is still pending
            this dialog is part of a blocking gate, so there is nothing to close
            back to except the choice itself.
          -->
          <button
            v-if="isDismissible"
            type="button"
            class="cookie-preferences__close shrink-0 tw-flex items-center justify-center w-8 h-8 rounded-lg"
            :aria-label="$t('cookie_consent_close')"
            @click="close"
          >
            <svg class="w-4 h-4" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24" aria-hidden="true">
              <path stroke-linecap="round" stroke-linejoin="round" d="M6 6l12 12M18 6L6 18"/>
            </svg>
          </button>
        </div>

        <!-- The toggles scroll while the buttons below stay in view. -->
        <div class="cookie-preferences__body px-6 sm:px-8">
          <div class="divide-y divide-slate-100 border-t border-slate-100">
            <!-- Strictly necessary storage is exempt from consent, so it has no toggle. -->
            <div class="tw-flex items-start justify-between gap-4 py-4">
              <div class="min-w-0">
                <span class="block text-sm font-semibold text-slate-900">{{ $t('cookie_category_necessary') }}</span>
                <p class="text-xs text-slate-500 leading-relaxed mt-1 mb-0">{{ $t('cookie_category_necessary_description') }}</p>
              </div>
              <span class="shrink-0 rounded-full bg-slate-100 text-slate-500 text-xs font-semibold px-2.5 py-1">
                {{ $t('cookie_category_always_on') }}
              </span>
            </div>

            <div
              v-for="category in categories"
              :key="category"
              class="tw-flex items-start justify-between gap-4 py-4"
            >
              <div class="min-w-0">
                <label class="block text-sm font-semibold text-slate-900 cursor-pointer" :for="`cookie-category-${category}`">
                  {{ $t(`cookie_category_${category}`) }}
                </label>
                <p class="text-xs text-slate-500 leading-relaxed mt-1 mb-0">{{ $t(`cookie_category_${category}_description`) }}</p>
              </div>

              <!-- The label wraps the input, so the switch itself is what you
                   click; the category name above labels it for screen readers. -->
              <label class="cookie-switch shrink-0">
                <input
                  :id="`cookie-category-${category}`"
                  v-model="selection[category]"
                  type="checkbox"
                  class="cookie-switch__input"
                >
                <span class="cookie-switch__track" aria-hidden="true">
                  <span class="cookie-switch__thumb"/>
                </span>
              </label>
            </div>
          </div>
        </div>

        <div class="cookie-preferences__footer p-6 sm:px-8 sm:pb-8 pt-4">
          <!-- While consent is pending this dialog cannot be dismissed either,
               so a failed save must not fail silently. -->
          <p v-if="error" class="cookie-preferences__error" role="alert">
            {{ $t('cookie_consent_error') }}
          </p>

          <div class="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <button
              type="button"
              class="cookie-preferences__button"
              :disabled="isSaving"
              @click="rejectEverything"
            >
              {{ $t('cookie_consent_reject_all') }}
            </button>
            <button
              type="button"
              class="cookie-preferences__button"
              :disabled="isSaving"
              @click="acceptEverything"
            >
              {{ $t('cookie_consent_accept_all') }}
            </button>
            <button
              type="button"
              class="cookie-preferences__button cookie-preferences__button--primary"
              :disabled="isSaving"
              @click="save"
            >
              {{ $t('cookie_consent_save') }}
            </button>
          </div>

          <!-- Way back to the three top-level options while consent is pending. -->
          <button
            v-if="!isDismissible"
            type="button"
            class="cookie-preferences__back"
            :disabled="isSaving"
            @click="close"
          >
            {{ $t('cookie_consent_back') }}
          </button>
        </div>
      </div>
    </div>
  </teleport>
</template>

<script>
  import {
    OPTIONAL_CATEGORIES,
    acceptAll,
    consentState,
    rejectAll,
    saveSelection,
  } from '../../util/cookie-consent'
  import { DEFAULT_URLS } from '../../constants/default-urls.js'
  import { lockScroll, unlockScroll } from './scroll-lock'
  import { focusFirst, trapTab } from './focus-trap'

  export default {
    name: 'CookieConsentPreferencesModal',
    data () {
      return {
        isOpen: false,
        consent: consentState(),
        categories: OPTIONAL_CATEGORIES,
        selection: {},
        error: false,
      }
    },
    computed: {
      isSaving () {
        return this.consent.saving
      },
      /** Reached from the footer or the policy page — closing is harmless there. */
      isDismissible () {
        return this.consent.decision !== null
      },
      cookiePolicyUrl () {
        return this.$store.state.config.cookiePolicyUrl || DEFAULT_URLS.COOKIE_POLICY
      },
    },
    methods: {
      open () {
        // Start from what is currently granted, so the dialog reflects reality.
        this.selection = OPTIONAL_CATEGORIES.reduce((selection, category) => {
          selection[category] = this.consent.granted[category] === true

          return selection
        }, {})
        this.error = false
        this.isOpen = true

        // Lets the blocking consent modal step aside instead of stacking.
        this.$bus.emit('cookie-preferences-opened')
      },
      close () {
        this.isOpen = false

        // Brings the blocking consent modal back if nothing was decided yet.
        this.$bus.emit('cookie-preferences-closed')
      },
      closeOnBackdrop () {
        if (this.isDismissible) {
          this.close()
        }
      },
      onBackdropMousedown (event) {
        // While the gate is up, a backdrop click must not pull focus out.
        if (!this.isDismissible) {
          event.preventDefault()
        }
      },
      onKeydown (event) {
        if (event.key === 'Escape' && this.isDismissible) {
          this.close()

          return
        }

        trapTab(event, this.$refs.dialog)
      },
      save () {
        return this.persist(() => saveSelection(this.selection))
      },
      acceptEverything () {
        return this.persist(acceptAll)
      },
      rejectEverything () {
        return this.persist(rejectAll)
      },
      /** Only close once the choice is actually stored. */
      async persist (save) {
        this.error = false

        try {
          await save()
          this.close()
        } catch (e) {
          this.error = true
          console.error('[COOKIE CONSENT] saving the decision failed', e)
        }
      },
    },
    watch: {
      isOpen (open) {
        if (open) {
          lockScroll()
          // Bound on the document so the trap survives a backdrop click.
          document.addEventListener('keydown', this.onKeydown, true)
          this.$nextTick(() => focusFirst(this.$refs.dialog))
        } else {
          unlockScroll()
          document.removeEventListener('keydown', this.onKeydown, true)
        }
      },
    },
    mounted () {
      this.$bus.on('open-cookie-preferences', this.open)

      // Also reachable from outside the SPA (e.g. a link inside the cookie
      // policy document), so withdrawing is always one click away.
      window.openCookiePreferences = this.open
    },
    beforeUnmount () {
      this.$bus.off('open-cookie-preferences', this.open)

      if (this.isOpen) {
        unlockScroll()
      }

      document.removeEventListener('keydown', this.onKeydown, true)

      if (window.openCookiePreferences === this.open) {
        delete window.openCookiePreferences
      }
    },
  }
</script>

<style lang="scss" scoped>
  @import "../../../scss/_variables.scss";

  .cookie-preferences__overlay {
    position: fixed;
    inset: 0;
    /* Above the blocking consent modal, which sits at 10000. */
    z-index: 10001;
    overflow-y: auto;
    background-color: rgba(15, 23, 42, .6);
    backdrop-filter: blur(3px);
    animation: cookie-preferences-fade .2s ease-out;
  }

  .cookie-preferences {
    max-height: calc(100vh - 2rem);
    overflow-y: auto;
    font-family: "Fira Sans", sans-serif;
    animation: cookie-preferences-rise .24s cubic-bezier(.16, 1, .3, 1);

    &:focus {
      outline: none;
    }
  }

  .cookie-preferences__body {
    overflow-y: auto;
    flex: 1 1 auto;
  }

  /* Separates the choice from the toggles it scrolls over. */
  .cookie-preferences__footer {
    border-top: 1px solid #F1F5F9;
  }

  .cookie-preferences__icon {
    background-color: var(--primary-community-extra-lightest, #f1f5f9);
    color: $color-primary;
  }

  .cookie-preferences__close {
    border: none;
    background-color: transparent;
    color: #94A3B8;
    cursor: pointer;
    transition: background-color .15s ease, color .15s ease;

    &:hover {
      background-color: #F1F5F9;
      color: #475569;
    }
  }

  /* ── Toggle switch ─────────────────────────────────────── */
  .cookie-switch {
    position: relative;
    display: inline-flex;
    align-items: center;
    cursor: pointer;
  }

  .cookie-switch__input {
    position: absolute;
    top: 50%;
    left: 50%;
    width: 1px;
    height: 1px;
    min-width: 0;
    margin: 0;
    padding: 0;
    border: none;
    background: none;
    opacity: 0;
    pointer-events: none;

    /* The global input[type=checkbox] rule paints a box through :after. */
    &:after {
      content: none;
    }
  }

  .cookie-switch__track {
    display: block;
    width: 42px;
    height: 24px;
    padding: 2px;
    border-radius: 999px;
    background-color: #CBD5E1;
    cursor: pointer;
    transition: background-color .18s ease, box-shadow .15s ease;
  }

  .cookie-switch__thumb {
    display: block;
    width: 20px;
    height: 20px;
    border-radius: 50%;
    background-color: white;
    box-shadow: 0 1px 3px rgba(15, 23, 42, .25);
    transition: transform .18s ease;
  }

  .cookie-switch__input:checked + .cookie-switch__track {
    background-color: $color-primary;

    .cookie-switch__thumb {
      transform: translateX(18px);
    }
  }

  .cookie-switch__input:focus-visible + .cookie-switch__track {
    box-shadow: 0 0 0 3px rgba(148, 163, 184, .45);
  }

  .cookie-preferences__error {
    margin: 0 0 .75rem;
    padding: .625rem .75rem;
    border-radius: .5rem;
    background-color: #FEF2F2;
    color: #B91C1C;
    font-size: .8125rem;
  }

  /* ── Buttons ───────────────────────────────────────────── */
  .cookie-preferences__button {
    padding: .625rem 1rem;
    border-radius: .625rem;
    border: 1px solid #E2E8F0;
    background-color: white;
    color: #334155;
    font-size: .875rem;
    font-weight: 600;
    cursor: pointer;
    white-space: nowrap;
    transition: background-color .15s ease, color .15s ease, border-color .15s ease;

    &:hover:not(:disabled) {
      background-color: #F8FAFC;
    }

    &:disabled {
      opacity: .55;
      cursor: default;
    }
  }

  .cookie-preferences__button--primary {
    background-color: $color-primary;
    border-color: $color-primary;
    color: white;

    &:hover:not(:disabled) {
      background-color: $color-primary-lighter;
      border-color: $color-primary-lighter;
    }
  }

  .cookie-preferences__back {
    display: block;
    width: 100%;
    margin-top: .75rem;
    padding: .5rem;
    border: none;
    background: none;
    color: #64748B;
    cursor: pointer;
    font-size: .8125rem;
    font-weight: 600;

    &:hover:not(:disabled) {
      color: #334155;
    }

    &:disabled {
      opacity: .55;
      cursor: default;
    }
  }

  @keyframes cookie-preferences-fade {
    from { opacity: 0; }
    to { opacity: 1; }
  }

  @keyframes cookie-preferences-rise {
    from {
      opacity: 0;
      transform: translateY(12px) scale(.97);
    }
    to {
      opacity: 1;
      transform: none;
    }
  }

  @media (prefers-reduced-motion: reduce) {
    .cookie-preferences__overlay,
    .cookie-preferences {
      animation: none;
    }
  }
</style>
